/**
 * Packaging Form Modal
 *
 * Modal for creating and editing packagings.
 * @see US-8.21 - Gestion des Conditionnements
 * @see PLAN_UNITE_AFFICHAGE_PRIX.md - Enrichissement avec relation Unit
 */

import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@/lib/zod-resolver';
import { createPackagingSchema } from '@bstock/shared';
import type { CreatePackagingInput, PackagingWithUsageCount } from '@bstock/shared';
import { Modal, ModalFooter, Button, Input, Select } from '@/components/ui';
import { getQuantityStep } from '@/lib/format';
import type { SelectOptionGroup } from '@/components/ui';
import { useCreatePackaging, useUpdatePackaging } from '../hooks/usePackagings';
import { useUnitsByType } from '../../raw-products/hooks/useUnits';

interface PackagingFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  packaging?: PackagingWithUsageCount | null;
}

type FormData = CreatePackagingInput;

export function PackagingFormModal({ isOpen, onClose, packaging }: PackagingFormModalProps) {
  const isEditing = !!packaging;

  const createMutation = useCreatePackaging();
  const updateMutation = useUpdatePackaging();
  const isLoading = createMutation.isPending || updateMutation.isPending;

  // Fetch units grouped by type for the select
  const { data: unitsByType, isLoading: unitsLoading } = useUnitsByType();

  // Build grouped options for the Select component
  const unitGroups: SelectOptionGroup[] = useMemo(() => {
    if (!unitsByType) return [];
    return [
      {
        label: 'Masse',
        options: unitsByType.MASS?.map((u) => ({ value: u.id, label: `${u.symbol} - ${u.name}` })) ?? [],
      },
      {
        label: 'Volume',
        options: unitsByType.VOLUME?.map((u) => ({ value: u.id, label: `${u.symbol} - ${u.name}` })) ?? [],
      },
      {
        label: 'Piece',
        options: unitsByType.PIECE?.map((u) => ({ value: u.id, label: `${u.symbol} - ${u.name}` })) ?? [],
      },
    ];
  }, [unitsByType]);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(createPackagingSchema),
    defaultValues: {
      name: '',
      unitId: '',
      quantity: 1,
    },
  });

  const watchedQuantity = watch('quantity');
  const watchedUnitId = watch('unitId');

  // Find selected unit for conversion preview
  const currentUnit = useMemo(() => {
    if (!unitsByType || !watchedUnitId) return null;
    const allUnits = [...unitsByType.MASS, ...unitsByType.VOLUME, ...unitsByType.PIECE];
    return allUnits.find((u) => u.id === watchedUnitId) ?? null;
  }, [unitsByType, watchedUnitId]);

  // Reset form when packaging changes
  useEffect(() => {
    if (packaging) {
      reset({
        name: packaging.name,
        unitId: packaging.unitId,
        quantity: packaging.quantity,
      });
    } else {
      reset({
        name: '',
        unitId: '',
        quantity: 1,
      });
    }
  }, [packaging, reset]);

  const onSubmit = async (data: FormData) => {
    try {
      if (isEditing && packaging) {
        await updateMutation.mutateAsync({ id: packaging.id, data });
      } else {
        await createMutation.mutateAsync(data);
      }
      onClose();
    } catch {
      // Error is handled by mutation
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditing ? 'Modifier le conditionnement' : 'Nouveau conditionnement'}
      size="sm"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input
          label="Nom du conditionnement *"
          {...register('name')}
          error={errors.name?.message}
          placeholder="Ex: Carton 24, Sac 25kg, Bidon 5L"
        />

        <Select
          label="Unite de mesure *"
          value={watchedUnitId}
          onChange={(value) => setValue('unitId', value)}
          groups={unitGroups}
          placeholder="Selectionner une unite..."
          error={errors.unitId?.message}
          disabled={unitsLoading}
        />

        <Input
          label={`Quantite ${currentUnit ? `(en ${currentUnit.symbol})` : ''} *`}
          type="number"
          step={getQuantityStep()}
          min="0.001"
          {...register('quantity', { valueAsNumber: true })}
          error={errors.quantity?.message}
          placeholder="Ex: 25"
        />

        {/* Conversion preview */}
        {currentUnit && watchedQuantity > 0 && currentUnit.conversionFactor !== 1 && (
          <div className="bg-neutral-50 rounded-lg p-3 text-sm text-neutral-600">
            <span className="font-medium">Conversion:</span>{' '}
            {watchedQuantity} {currentUnit.symbol} ={' '}
            {(watchedQuantity * currentUnit.conversionFactor).toLocaleString('fr-FR')} {currentUnit.type === 'MASS' ? 'g' : currentUnit.type === 'VOLUME' ? 'ml' : 'pce'}
          </div>
        )}

        <p className="text-xs text-neutral-500">
          Le conditionnement definit comment un produit est achete (ex: "Sac 25kg" = 25 kg).
          La conversion vers l'unite recette sera calculee automatiquement.
        </p>

        <ModalFooter>
          <Button type="button" variant="ghost" onClick={handleClose} disabled={isLoading}>
            Annuler
          </Button>
          <Button type="submit" isLoading={isLoading}>
            {isEditing ? 'Modifier' : 'Creer'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
